import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ShellTodayService } from '../../shell-clock/shell-today.service';
import { parseOptionTicker } from '../../options-common/options.model';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DetectMethodChanges } from '../../utils';
import { Solution } from './copilot-domain';
import { CoPilotPositionDto } from './copilot-operations';
import { buildCopilotTransactionRows, CopilotTxRow } from './copilot-transactions-view';

// Read-only Transactions popup for a CoPilot Solution card (CR-20260619-001). Shows the solution's
// BEFORE -> TRANSACTION -> AFTER, mirroring the Hedge Matrix "After State Preview" dialog
// (positions-section/after-state-preview), scoped to ONE solution = ONE hedge.
//
// READ-ONLY: NO copy, NO Apply, NO edit (FR-92/93) — the only broker path is the grid's existing
// right-click copy. The view is a PURE function of the Solution snapshot (no facade/grid read; see
// copilot-transactions-view). NO prices (owner ruling 2026-06-22).
//
// Opened IMPERATIVELY via show() — this codebase drives change detection through the @DetectMethodChanges
// decorator (which calls this._changeDetector.detectChanges() in a finally), NOT automatic CD / markForCheck.
// So visibility is set directly on the dx-popup ViewChild (no [visible] binding), exactly like
// HgAfterStatePreviewComponent.
@Component({
    selector: 'ets-hg-copilot-transactions-popup',
    templateUrl: './hg-copilot-transactions-popup.component.html',
    styleUrls: ['./hg-copilot-transactions-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgCopilotTransactionsPopupComponent implements OnDestroy {

    constructor(private readonly _changeDetector: ChangeDetectorRef, private readonly _clock: ShellTodayService) {
        this._clockSubscription = _clock.changed$.subscribe(() => this.updateReferenceDates());
    }

    private readonly _clockSubscription: Subscription;
    private _viewRevision = 0;
    private _underlyings: string[] = [];
    private _referenceDates: (string | null)[] = [];
    referenceDate: string | null = null;

    referenceDateFor(row: CopilotTxRow): string | null { return this._referenceDates[this.rows.indexOf(row)] || null; }

    private updateReferenceDates(): void {
        this._referenceDates = this._underlyings.map(underlying => this._clock.todayKey(underlying));
        this.referenceDate = this._referenceDates[0] || null;
        if (this.popup?.visible) { this._changeDetector.detectChanges(); }
    }

    ngOnDestroy(): void { ++this._viewRevision; this._clockSubscription.unsubscribe(); }

    @ViewChild(DxPopupComponent) popup: DxPopupComponent;

    rows: CopilotTxRow[] = [];

    title = 'Transactions';

    // Fixed popup height (a dx-scroll-view inside a flex:1 container needs a bounded popup height, else it
    // collapses to 0 — mirrors HgAfterStatePreviewComponent.height).
    get height(): number {
        return this.rows.length > 1 ? 560 : 440;
    }

    @DetectMethodChanges()
    // `held` = the working-state book as read when the solution's run was fired (CR-20260918-001);
    // an Adjust package's BEFORE column comes from it. Optional: a Close needs none.
    show(solution: Solution, held?: CoPilotPositionDto[]): void {
        this.rows = buildCopilotTransactionRows(solution, held);
        const revision = ++this._viewRevision;
        this._underlyings = this.rows.map((row, index) => {
            const legs = solution.content.cards?.[index]?.legs || solution.content.legs;
            const leg = legs.find(l => l.groupId === row.hedgeId) || legs[0];
            return leg?.asset || parseOptionTicker(leg?.ticker)?.underlying;
        });
        this.updateReferenceDates();
        Promise.all(Array.from(new Set(this._underlyings)).map(underlying => this._clock.refresh(underlying)))
            .then(() => { if (revision === this._viewRevision) { this.updateReferenceDates(); } }).catch(() => {});
        const name = solution && solution.content ? solution.content.displayName : '';
        this.title = name ? 'Transactions - ' + name : 'Transactions';
        this.popup.visible = true;
    }

    @DetectMethodChanges()
    hide(): void {
        ++this._viewRevision;
        this.popup.visible = false;
    }
}
